import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { selectInputs } from '../redux/inputsReducer';
import { selectDealerAndVehicleData } from '../redux/dealerAndVehicleReducer';
import { selectAppConfig } from '../redux/appConfigReducer';
import { ELSP_URL } from '../constants/environment';
import { SubmissionData } from '../types';
import { prepareDealerIdForFormsApi } from '../functions/dealerIdConverter';
import { US_MARKET, FEATURE_APP_DETAILS } from '../constants';
import { reversedPhoneNumberFormatter } from '../functions/phoneNumberFormatter';
import { convertToPromotionsType } from '../functions/convertToPromotionsType';
import useSourceOfLead from './useSourceOfLead';
import { digitalLeadsFormSubmissionError } from '../utils/analyticsEventListeners';
import { selectForm } from '../redux/formReducer';
import formTypeFormatter from '../functions/formTypeFormatter';
import {
  getOffersDataToSubmit,
  getOffersVehicleDataToSubmit,
} from '../functions/getOffersDataToSubmit';
import { getVehicleDataToSubmit } from '../functions/getVehicleDataToSubmit';
import consentProcessor from '../functions/consentProcessor';
import getHiddenInputs from '../functions/getHiddenInputs';

export const useElspFormSubmission = () => {
  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    zipCode,
    preferredContactMethod,
    preferredContactTime,
    ccpa,
    tcpaConsent,
    universalConsent,
    dealerConsent,
    oemConsent,
    topicOfInterest,
  } = useSelector(selectInputs);

  const { vehicle, vehicleByAudiCode, vehicleByCarline } = useSelector(selectDealerAndVehicleData);
  const { dealerId, market, adobeCloudId, device, ctaInnerText, campaignId, offers, requestType } =
    useSelector(selectAppConfig);
  const contactForm = useSelector(selectForm);

  const isUSMarket = market === US_MARKET || market?.toLowerCase().includes('us');
  const isAfterSales = topicOfInterest === 'service' || topicOfInterest === 'parts';

  const { url, specificSource, requestSource } = useSourceOfLead(topicOfInterest);

  const promotion = convertToPromotionsType(offers!);
  const finalDealerId = dealerId || vehicle?.dealer?.id;
  const isVehicleInfoAvailable = !!vehicle;
  const isTier3 = requestSource === 'tier_3';

  // ---------------------- Dealer/ZIP Info to submit -----------------------//

  const dealerToSubmit = finalDealerId && prepareDealerIdForFormsApi(finalDealerId);
  const correctZip =
    isUSMarket && zipCode.length > 5
      ? zipCode
      : zipCode?.toUpperCase().replace(/(\w{3})(\w{3})/, '$1 $2');

  // ------------------------- Consent Processor --------------------------//

  const combinedConsents = {
    universal: universalConsent,
    ccpa,
    tcpa: tcpaConsent,
    dealer: dealerConsent,
    oem: oemConsent,
  };
  const consents = consentProcessor(isTier3, isUSMarket, combinedConsents);

  // ---------------------- Vehicle Info to submit -----------------------//

  const vehicleToSubmit = getVehicleDataToSubmit({
    vehicle,
    vehicleByAudiCode,
    vehicleByCarline,
    isTier3,
  });

  // ---------------------- Offer Details to submit -----------------------//

  const offerDataToSubmit = getOffersDataToSubmit(offers);

  const offerVehicleDataToSubmit = getOffersVehicleDataToSubmit(offers);

  // ---------------------- Hidden Inputs -----------------------//
  // This is just for one dealer group
  const timestampAndGa = getHiddenInputs();

  // ------------------------ Full submition data --------------------------//

  const submissionData: SubmissionData = {
    additionalInfo: {
      device: 'Desktop',
    },
    comments: timestampAndGa,
    provider: {
      language: 'EN',
      requestSource,
      url,
      requestCategory: 'LEAD', // hardcoded
      requestType: formTypeFormatter(
        isVehicleInfoAvailable,
        specificSource,
        topicOfInterest,
        requestType,
      ),
      adobeCloudID: adobeCloudId || '',
      eventName: ctaInnerText || '', // ctaVerbiage in the past
      campaignId: campaignId || '',
    },
    customer: {
      firstName,
      lastName,
      preferredContactMethod,
      preferredContactTime,
      email,
      phone: phoneNumber ? reversedPhoneNumberFormatter(phoneNumber) : '',
      address: {
        zip: correctZip,
      },
    },
    consents: consents || [],
    originCountry: isUSMarket ? 'US' : 'CA',
    brand: 'AU', // hardcoded
    links: [
      {
        name: 'Lead Details',
        url,
      },
    ],
    ...(offerDataToSubmit ? { offer: offerDataToSubmit } : {}),
    ...(vehicleToSubmit ? { vehicles: [vehicleToSubmit] } : []),
    ...(offerVehicleDataToSubmit ? { vehicle: offerVehicleDataToSubmit } : {}),
    ...(dealerToSubmit ? { dealer: { dealerId: dealerToSubmit } } : {}),
  };

  interface AxiosLikeError extends Error {
    response?: {
      status: number;
      data: any;
    };
  }

  const ELSPGateway = useMemo(() => {
    if (isAfterSales) return `${ELSP_URL}/v1/aftersales/leads`;
    return `${ELSP_URL}/v2/leads`;
  }, [isAfterSales]);

  const handleSubmit = async () => {
    try {
      const response = await fetch(ELSPGateway, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.text().catch(() => '');
        const error: AxiosLikeError = new Error(`HTTP error! status: ${response.status}`);
        error.response = {
          status: response.status,
          data: errorData,
        };
        throw error;
      }

      const data = await response.json();
      return data;
      // eslint-disable-next-line
    } catch (error: any) {
      // eslint-disable-next-line
      console.log(error);
      const errorPayload = {
        error_message: error.response?.data,
        error_code: error.response?.status,
      };
      digitalLeadsFormSubmissionError(
        contactForm,
        vehicle!,
        FEATURE_APP_DETAILS,
        promotion,
        errorPayload,
      );
      return error;
    }
  };

  return handleSubmit;
};
